import {
  Chip,
  DetailShell,
  Field,
  SectionLabel,
  T,
  fmtDate,
  humanise,
  personLabel,
} from './shared';

/**
 * The detail drawer content for one decision. `detail` is
 * `{ decision: {...} }`. Same header/body/footer shape as
 * `RiskDetailPanel` via `DetailShell` — decisions have no write endpoint
 * yet, so the footer is a plain note instead of action buttons.
 */
const DecisionDetailPanel = ({ detail, onClose }) => {
  if (!detail?.decision) return null;
  const decision = detail.decision;

  return (
    <DetailShell
      chips={
        <>
          <Chip bordered>{humanise(decision.decision_type)}</Chip>
          <Chip bordered>{humanise(decision.scope)}</Chip>
        </>
      }
      statusChip={
        decision.is_reversal ? (
          <Chip fg={T.warn} bg={T.warnBg}>
            Reversal
          </Chip>
        ) : null
      }
      title={decision.decision_text}
      raisedIn={
        decision.created_at
          ? `A meeting transcript · ${fmtDate(decision.created_at)}`
          : 'A meeting transcript'
      }
      quote={decision.evidence?.quote}
      onClose={onClose}
      footer={
        <span className="text-[12px]" style={{ color: T.muted }}>
          No actions available yet
        </span>
      }
    >
      {decision.rationale && (
        <div className="mb-5">
          <SectionLabel>Rationale</SectionLabel>
          <p className="text-[13px]" style={{ color: T.textSec }}>
            {decision.rationale}
          </p>
        </div>
      )}

      <div
        className="mb-5 grid grid-cols-2 gap-x-5 gap-y-4 pb-5"
        style={{ borderBottom: `1px solid ${T.br}` }}
      >
        <Field label="Proposed by" value={personLabel(decision.proposed_by)} />
        <Field
          label="Decided by"
          value={
            decision.decided_by?.length
              ? decision.decided_by.map(personLabel).join(', ')
              : null
          }
        />
        <Field
          label="Confidence"
          value={
            decision.decision_confidence != null
              ? `${Math.round(decision.decision_confidence * 100)}%`
              : null
          }
        />
        <Field
          label="Linked deliverable"
          value={decision.linked_deliverable_id}
        />
      </div>

      {decision.alternatives_considered?.length > 0 && (
        <div className="mb-5">
          <SectionLabel count={decision.alternatives_considered.length}>
            Alternatives considered
          </SectionLabel>
          <ul className="space-y-1.5">
            {decision.alternatives_considered.map((alt, index) => (
              <li
                key={index}
                className="flex gap-2 text-[13px]"
                style={{ color: T.textSec }}
              >
                <span style={{ color: T.muted }}>·</span>
                <span className="min-w-0">{alt}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {decision.supersedes_decision_id && (
        <div>
          <SectionLabel>Supersedes</SectionLabel>
          <p className="font-mono text-[13px]" style={{ color: T.textSec }}>
            {decision.supersedes_decision_id}
          </p>
        </div>
      )}
    </DetailShell>
  );
};

export default DecisionDetailPanel;
